import { nanoid } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { FragmentGroup } from "../../media/FragmentGroup";
import { MediaSource } from "../../media/MediaSource";
import { Entity, EntityImage, EntityImageCollection, FileVideoFrameEntityImage, ThumbnailEntityImage } from "../Entity";
import { DataDescription } from "../dataDescriptions/DataDescription";
import { DateEntityProperty } from "../entityProperties/DateEntityProperty";
import { StringEntityProperty } from "../entityProperties/StringEntityProperty";
import { EntityAdapter } from "./EntityAdapter";
import { NumberListEntityProperty } from "../entityProperties/NumberListEntityProperty";
import { AreaBounds } from "cloud-core/spatial/Spatial";

export type FragmentGroupWithMedia = { group: FragmentGroup; media: MediaSource; runId: number };

export class FragmentGroupEntityAdapter implements EntityAdapter<FragmentGroupWithMedia> {
    dataDescription: DataDescription;

    constructor(dataDescription: DataDescription) {
        this.dataDescription = dataDescription;
    }

    setDataDescription(dataDescription: DataDescription): void {
        this.dataDescription = dataDescription;
    }

    convertToEntity(data: FragmentGroupWithMedia): Entity | undefined {
        if (data?.media?.files?.initialDisplay?.fileId === undefined) {
            return undefined;
        }

        // TODO: remove temp fix
        // Temp fix for bounds with wrong order
        const bounds = data.group.bounds;
        const corrected_bounds: AreaBounds = [
            Math.min(bounds[0], bounds[2]),
            Math.min(bounds[1], bounds[3]),
            Math.max(bounds[0], bounds[2]),
            Math.max(bounds[1], bounds[3]),
        ];

        let mainImage: EntityImage;
        const videoFrameImage = new FileVideoFrameEntityImage({
            timestamp: data.media.startsAt - data.group.startsAt,
            bounds: corrected_bounds,
            fileId: data.media.files.initialDisplay.fileId,
        });

        if (data.group.eventType !== "movement" && data.group.info?.midFragmentId) {
            mainImage = new ThumbnailEntityImage({
                mediaId: data.media.mediaId,
                runId: data.runId,
                fragGroupId: data.group.fragmentGroupId,
                fragmentId: data.group.primaryFragId ?? data.group.info.midFragmentId,
                timestamp: data.media.startsAt - data.group.startsAt,
                backupImage: videoFrameImage,
            });
        } else {
            mainImage = videoFrameImage;
        }

        let images = [mainImage];
        let frameRate = 10;

        if (data.group?.info !== undefined && "frameRate" in data.group.info) {
            frameRate = data.group.info.frameRate;
        }

        if (data.group.info?.mediaType === "frames") {
            images = data.group.info.frameFragmentIds.map(
                (fragId) =>
                    new ThumbnailEntityImage({
                        mediaId: data.media.mediaId,
                        runId: data.runId,
                        fragGroupId: data.group.fragmentGroupId,
                        fragmentId: fragId,
                        timestamp: 2,
                        backupImage: videoFrameImage,
                    }),
            );
            frameRate = data.group.info.frameRate ?? frameRate;
        }

        const entityImageCollection = new EntityImageCollection(images, frameRate);

        if (data.group.info?.mediaType === "video") {
            entityImageCollection.setVideoModeToVideo(data.group.info.videoFileId, data.group.count);
        }

        return new Entity({
            id: nanoid(16),
            sourceObject: {
                id: data.group.fragmentGroupId,
                mediaSource: data.media,
                mediaSourceFrameOffset: data.group.startsAt - data.media.startsAt,
                runId: data.runId,
                projectId: data.media.projectId,
                object: data.group,
            },
            dataDescription: this.dataDescription,
            images: entityImageCollection,
            properties: {
                BoundsBitmap: new StringEntityProperty(data.group.bitmap),
                LowResBoundsBitmap: new StringEntityProperty(data.group.lowResBitmap),
                Bounds: new NumberListEntityProperty(corrected_bounds),
                EventType: new StringEntityProperty(data.group.eventType),
                StartTime: new DateEntityProperty(dayjs(data.group.startsAt).toDate()),
                EndTime: new DateEntityProperty(dayjs(data.group.endsAt).toDate()),
                FragmentGroupId: new StringEntityProperty(data.group.fragmentGroupId),
                Comments: new StringEntityProperty(data.group.primaryFragId),
            },
        });
    }
}

export class MovementFragmentGroupEntityAdapter extends FragmentGroupEntityAdapter {
    convertToEntity(data: FragmentGroupWithMedia): Entity | undefined {
        if (data.group.eventType !== "movement") {
            return undefined;
        }

        return super.convertToEntity(data);
    }
}

export class ObjectClassificationFragmentGroupEntityAdapter extends FragmentGroupEntityAdapter {
    convertToEntity(data: FragmentGroupWithMedia): Entity | undefined {
        if (data.group.eventType !== "object") {
            return undefined;
        }

        const entity = super.convertToEntity(data);
        if (entity === undefined) {
            return undefined;
        }

        const confidence = Number(data.group.metadata.ObjectCategoryConf);

        entity.properties["ObjectCategory"] = new StringEntityProperty(
            data.group.metadata.ObjectCategory,
            true,
            isNaN(confidence) ? undefined : confidence,
        );

        return entity;
    }
}
